import React, { useState, lazy, Suspense } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";
import {
  autoCountDefaultConfig,
  easyParcelDefaultConfig,
  gdexDefaultConfig,
  kexExpressDefaultConfig,
  ninjaVanDefaultConfig,
  posLajuDefaultConfig,
  skynetDefaultConfig,
  sqlAccDefaultConfig,
} from "../../helpers/ExportDefaultFields";

const ExportDialog = lazy(() => import("./ExportDialog"));
const GeneralDialog = lazy(() => import("./GeneralDialog"));

export default function ExportMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openExportStandard, setOpenExportStandard] = useState(false);
  const [dialogs, setDialogs] = useState({
    EasyParcel: false,
    Skynet: false,
    AutoCount: false,
    PosLaju: false,
    GDEX: false,
    KEXExpress: false,
    NinjaVan: false,
    SQLAccounting: false,
  });

  const isBusinessPlan = useIsBusinessPlan();
  const open = Boolean(anchorEl);

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const openDialog = (key) => setDialogs((prev) => ({ ...prev, [key]: true }));
  const closeDialog = (key) =>
    setDialogs((prev) => ({ ...prev, [key]: false }));

  const dialogConfigs = [
    {
      key: "EasyParcel",
      label: "Easy parcel",
      config: {
        dialogTitle: "Fill in the Easy Parcel details",
        menuItemName: "EasyParcel",
        cacheConfigName: "easyParcelConfig",
        defaultConfig: easyParcelDefaultConfig,
        fileName: "EASY_PARCEL",
        itemPerLine: false,
      },
    },
    {
      key: "Skynet",
      label: "Skynet",
      config: {
        dialogTitle: "Fill in the Skynet details",
        menuItemName: "Skynet",
        cacheConfigName: "skynetConfig",
        defaultConfig: skynetDefaultConfig,
        fileName: "SKYNET",
        itemPerLine: false,
      },
    },
    {
      key: "AutoCount",
      label: "AutoCount",
      config: {
        dialogTitle: "Fill in the AutoCount details(1 item 1 line)",
        menuItemName: "AutoCount",
        cacheConfigName: "autoCountConfig",
        defaultConfig: autoCountDefaultConfig,
        fileName: "AUTOCOUNT",
        itemPerLine: true,
      },
    },
    {
      key: "PosLaju",
      label: "PosLaju",
      config: {
        dialogTitle: "Fill in the POS Laju details",
        menuItemName: "POS Laju",
        cacheConfigName: "posLajuConfig",
        defaultConfig: posLajuDefaultConfig,
        fileName: "POS_LAJU",
        itemPerLine: false,
      },
    },
    {
      key: "GDEX",
      label: "GDEX",
      config: {
        dialogTitle: "Fill in the GDEX details",
        menuItemName: "GDEX",
        cacheConfigName: "gdexConfig",
        defaultConfig: gdexDefaultConfig,
        fileName: "GDEX",
        itemPerLine: false,
      },
    },
    {
      key: "KEXExpress",
      label: "KEX Express",
      config: {
        dialogTitle: "Fill in the KEX Express details",
        menuItemName: "KEX Express",
        cacheConfigName: "kexExpressConfig",
        defaultConfig: kexExpressDefaultConfig,
        fileName: "KEX_EXPRESS",
        itemPerLine: false,
      },
    },
    {
      key: "NinjaVan",
      label: "Ninja Van",
      config: {
        dialogTitle: "Fill in the Ninja Van details",
        menuItemName: "Ninja Van",
        cacheConfigName: "ninjaVanConfig",
        defaultConfig: ninjaVanDefaultConfig,
        fileName: "NINJA_VAN",
        itemPerLine: false,
      },
    },
    {
      key: "SQLAccounting",
      label: "SQL Accounting",
      config: {
        dialogTitle: "Fill in the SQL Accounting details(1 item 1 line)",
        dialogDescription: (
          <h3>
            Please refer{" "}
            <a
              href="https://docs.google.com/spreadsheets/d/1TcFVqH1VCJ5SjHAt2VCHIeTTcQVUjtIBxLaZGGM0E5k/edit?gid=1579284888#gid=1579284888"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>{" "}
            on how to fill up the file
          </h3>
        ),
        menuItemName: "SQL Accounting",
        cacheConfigName: "sqlAccConfig",
        defaultConfig: sqlAccDefaultConfig,
        fileName: "SQL Accounting",
        itemPerLine: true,
      },
    },
  ];

  const renderDialog = (key, config) =>
    dialogs[key] && (
      <Suspense fallback={null}>
        <GeneralDialog
          open={dialogs[key]}
          setOpen={() => closeDialog(key)}
          {...config}
        />
      </Suspense>
    );

  return (
    <div>
      <IconButton onClick={handleClick}>
        <DownloadIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            setOpenExportStandard(true);
          }}
        >
          Export Standard
        </MenuItem>

        {dialogConfigs.map(({ key, label }) => (
          <MenuItem
            key={key}
            onClick={() => {
              handleClose();
              openDialog(key);
            }}
            disabled={!isBusinessPlan}
          >
            {label + (isBusinessPlan ? "" : " (Required plan upgrade)")}
          </MenuItem>
        ))}
      </Menu>

      {/* Standard Dialog */}
      {openExportStandard && (
        <Suspense fallback={null}>
          <ExportDialog
            open={openExportStandard}
            setOpen={setOpenExportStandard}
          />
        </Suspense>
      )}

      {/* Dynamic General Dialogs */}
      {dialogConfigs.map(({ key, config }) => renderDialog(key, config))}
    </div>
  );
}
